/** Modale catalogue — 2 lettres, puis la liste. N’ouvre rien dans la page. */
import { useEffect, useMemo, useRef, useState } from "react";
import { facturxFetch } from "../../api/client";
import { useFormatters } from "../../formats/useFormatters";
import { asProduct, type Product } from "../catalog/types";
import { isActive } from "../catalog/format";
import "./quotes.css";

type QuoteCatalogProps = {
  companyId: number;
  onPick: (p: Product) => void;
  onClose: () => void;
};

export function QuoteCatalog({ companyId, onPick, onClose }: QuoteCatalogProps) {
  const { money } = useFormatters();
  const [rows, setRows] = useState<Product[]>([]);
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setQ("");
    setErr("");
    facturxFetch<{ products: Product[] }>(`products.php?company_id=${companyId}`)
      .then((data) => setRows((data.products || []).map(asProduct).filter(isActive)))
      .catch((e) => setErr(e instanceof Error ? e.message : "Catalogue indisponible"));
    const t = window.setTimeout(() => inputRef.current?.focus(), 40);
    return () => window.clearTimeout(t);
  }, [companyId]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const visible = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (s.length < 2) return [];
    return rows.filter((p) => [p.label, p.product_code, p.description].some((v) => (v || "").toLowerCase().includes(s)));
  }, [rows, q]);

  const ready = q.trim().length >= 2;

  return (
    <div className="fx-quote-cat-back" role="dialog" aria-label="Catalogue" onClick={onClose}>
      <div className="fx-quote-cat" onClick={(e) => e.stopPropagation()}>
        <header className="fx-quote-cat__head">
          <p>Catalogue</p>
          <button type="button" className="fx-quote-icon" onClick={onClose} aria-label="Fermer">
            ×
          </button>
        </header>
        <input
          ref={inputRef}
          className="fx-q-ctrl"
          value={q}
          placeholder="2 ou 3 lettres…"
          autoComplete="off"
          onChange={(e) => setQ(e.target.value)}
        />
        {err ? <p className="fx-quote-cat__hint is-err">{err}</p> : null}
        {!err && !ready ? <p className="fx-quote-cat__hint">Tapez pour lister les produits.</p> : null}
        {ready && visible.length === 0 ? <p className="fx-quote-cat__hint">Aucun produit.</p> : null}
        <ul className="fx-quote-cat__list">
          {visible.map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="fx-quote-cat__row"
                onClick={() => {
                  onPick(p);
                  onClose();
                }}
              >
                <strong>{p.label}</strong>
                <span>
                  {p.product_code ? `${p.product_code} · ` : ""}
                  {money(p.unit_price_ht_cents)} HT
                  {p.unit ? ` / ${p.unit}` : ""}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
